
import { RealtimeChannel, SupabaseClient } from "@supabase/supabase-js";
import { logger } from "@/utils/logger";

export type ChannelStatus = 'CONNECTING' | 'CONNECTED' | 'DISCONNECTED' | 'ERROR';

export const setupRealtimeConnection = async (
  supabase: SupabaseClient,
  channelName: string,
  userId: string,
  callbacks: {
    onConnect?: () => void;
    onDisconnect?: () => void;
    onError?: (error: unknown) => void;
    onNotification?: (payload: unknown) => void;
  }
) => {
  try {
    logger.debug(`Setting up realtime connection for channel: ${channelName}`);
    
    // Security: we intentionally do NOT enable broadcast or presence on this channel.
    // The notification stream relies on postgres_changes, which is enforced by the
    // RLS policy on public.notifications (user_id = auth.uid()). Broadcast/presence
    // would expose channel traffic that bypasses table RLS.
    const channel = supabase.channel(channelName);

    channel.on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'notifications',
      filter: `user_id=eq.${userId}`,
    }, (payload) => {
      logger.debug('Database notification change received:', payload);
      if (callbacks.onNotification) {
        callbacks.onNotification(payload);
      }
    });

    const status = await channel.subscribe((status) => {
      logger.debug(`Notification channel status: ${status}`);
      
      if (status === 'SUBSCRIBED' && callbacks.onConnect) {
        callbacks.onConnect();
      } else if (status === 'CLOSED' && callbacks.onDisconnect) {
        callbacks.onDisconnect();
      } else if ((status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') && callbacks.onError) {
        logger.error(`Channel error for ${channelName}: ${status}`);
        callbacks.onError(status);
        
        setTimeout(() => {
          logger.debug(`Attempting to reconnect channel: ${channelName}`);
          try {
            channel.subscribe();
          } catch (err) {
            logger.error(`Error reconnecting to channel ${channelName}:`, err);
          }
        }, 5000);
      }
    });

    return {
      channel,
      status,
      unsubscribe: () => {
        logger.debug(`Unsubscribing from channel: ${channelName}`);
        try {
          channel.unsubscribe();
          supabase.removeChannel(channel);
        } catch (err) {
          logger.error(`Error unsubscribing from channel ${channelName}:`, err);
        }
      }
    };
  } catch (error) {
    logger.error('Error setting up realtime connection:', error);
    if (callbacks.onError) {
      callbacks.onError(error);
    }
    return {
      channel: null,
      status: 'ERROR' as const,
      unsubscribe: () => {}
    };
  }
};

export const invokeFunctionWithRetry = async (
  supabase: SupabaseClient,
  functionName: string,
  payload: unknown = {},
  maxRetries: number = 3
) => {
  let retries = 0;
  let lastError: unknown = null;

  while (retries < maxRetries) {
    try {
      logger.debug(`Invoking function ${functionName}, attempt ${retries + 1}/${maxRetries}`);
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: payload
      });

      if (error) {
        logger.error(`Error invoking ${functionName}:`, error);
        lastError = error;
        retries++;
        await new Promise(resolve => setTimeout(resolve, 1000 * retries));
      } else {
        logger.debug(`Successfully invoked ${functionName}`);
        return { data, error: null };
      }
    } catch (err) {
      logger.error(`Unexpected error invoking ${functionName}:`, err);
      lastError = err;
      retries++;
      await new Promise(resolve => setTimeout(resolve, 1000 * retries));
    }
  }

  return { data: null, error: lastError };
};
